import { ActivityIndicator, TouchableOpacity, View, Text, StyleSheet } from 'react-native'
import { WebView, type WebViewNavigation } from 'react-native-webview'
import { type ComponentProps, useRef, useState } from 'react'
import { SafeAreaView } from 'react-native-safe-area-context'
import { ChevronLeft } from '@/src/assets/icons/ChevronLeft'
import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import RefreshIcon from '@/src/assets/icons/RefreshIcon'
import Close from '@/src/assets/icons/Close'
import appBrowserBrowserFileUri from '@/src/common/utils/appBrowserFileUri'
import { mesaColorNames, typography } from '@/src/common/theme'

export function InAppBrowser({
  title,
  uri,
  onBackPress,
  injectJavaScript = '',
  safeAreaEdges,
}: {
  title: string
  uri: string
  onBackPress: () => void
  injectJavaScript?: string
  safeAreaEdges?: ComponentProps<typeof SafeAreaView>['edges']
  headerEnabled?: boolean
}) {
  const webviewRef = useRef<WebView>(null)
  const [loading, setLoading] = useState(true)

  const [canGoBack, setCanGoBack] = useState(false)
  const [canGoForward, setCanGoForward] = useState(false)

  const onNavigationStateChange = (navState: WebViewNavigation) => {
    setCanGoBack(navState.canGoBack)
    setCanGoForward(navState.canGoForward)
  }
  const source = appBrowserBrowserFileUri(uri)

  return (
    <SafeAreaView style={styles.container} edges={safeAreaEdges}>
      <HeaderSection
        title={title}
        onClosePress={onBackPress}
        onRefreshPress={() => {
          webviewRef.current?.reload()
        }}
      />
      <View style={styles.webViewContainer}>
        {loading && (
          <ActivityIndicator style={styles.activityIndicator} size="large" color={'#0C5551'} />
        )}
        <WebView
          ref={webviewRef}
          onLoad={() => {
            setLoading(false)
            if (webviewRef.current && injectJavaScript) {
              webviewRef.current.injectJavaScript(injectJavaScript)
            }
          }}
          source={{ uri: source }}
          style={styles.webView}
          onNavigationStateChange={onNavigationStateChange}
        />
      </View>
      <FooterSection
        canGoBack={canGoBack}
        onBackPress={() => webviewRef.current && webviewRef.current.goBack()}
        canGoForward={canGoForward}
        onForwardPress={() => webviewRef.current && webviewRef.current.goForward()}
      />
    </SafeAreaView>
  )
}

interface HeaderSectionInterface {
  title: string
  onClosePress: () => void
  onRefreshPress: () => void
}

const HeaderSection = ({ title, onClosePress, onRefreshPress }: HeaderSectionInterface) => (
  <View>
    <View style={styles.header}>
      <TouchableOpacity onPress={onClosePress} testID="InAppBrowser.Press.Back">
        <Close height={24} color={mesaColorNames['Mesa - Brand/Blackish #222222']} />
      </TouchableOpacity>
      <Text style={[typography['heading/heading4'], styles.title]}>{title}</Text>
      <TouchableOpacity onPress={onRefreshPress} style={styles.refreshIcon}>
        <RefreshIcon height={24} color={mesaColorNames['Mesa - Brand/Blackish #222222']} />
      </TouchableOpacity>
    </View>
  </View>
)

interface FooterSectionInterface {
  canGoBack: boolean
  onBackPress: () => void
  canGoForward: boolean
  onForwardPress: () => void
}

const FooterSection = ({
  canGoBack,
  onBackPress,
  canGoForward,
  onForwardPress,
}: FooterSectionInterface) => {
  const enabledColor = mesaColorNames['Mesa - Brand/Blackish #222222']
  /** https://github.com/react-native-elements/react-native-elements/blob/78e3994a67510d0666961e927c8e009468d6ce08/packages/base/src/helpers/colors.ts#L57 */
  const disabledColor = '#e3e6e8'

  return (
    <View>
      <View style={styles.footer}>
        <TouchableOpacity onPress={onBackPress} disabled={!canGoBack}>
          <ChevronLeft
            height={18}
            strokeWidth={3.0}
            color={canGoBack ? enabledColor : disabledColor}
          />
        </TouchableOpacity>
        <TouchableOpacity
          onPress={onForwardPress}
          disabled={!canGoForward}
          style={{ marginHorizontal: 40 }}
        >
          <ChevronRight
            height={18}
            strokeWidth={3.0}
            color={canGoForward ? enabledColor : disabledColor}
          />
        </TouchableOpacity>
        <View style={{ flex: 1 }} />
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: 'white' },
  sectionStyle: { paddingBottom: 4 },
  webView: { width: '100%' },
  activityIndicator: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    paddingHorizontal: 16,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    paddingBottom: 16,
  },
  refreshIcon: { alignItems: 'flex-end', justifyContent: 'center' },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 16,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  title: {
    color: '#0C5551',
    textAlign: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  webViewContainer: {
    flex: 1,
    position: 'relative',
  },
})
